import { Component, OnInit } from '@angular/core';
import { IFormGroup } from '@shared-libs/interfaces';
import { Action, NavigationDataKey, NavigationType, UserPermissions } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IDeviceType } from '@internal-models/device-type.model';
import { DeviceTypeProvider } from '@internal-providers/device-type.provider';
import { DeviceStatus, IDevice } from '@internal-models/device.model';
import { DeviceTypeComponent } from '@internal-components/administration-components/device-type-components/device-type-component/device-type.component';
import { DeviceProvider } from '@internal-providers/device.provider';
import { DeviceFormFactory } from '@internal-factories/forms/device-form.factory';
import { ValidationService } from '@shared-services/validation.service';
import { LoanComponent } from '../loan-component/loan.component';
import { ILoan } from '@internal-models/loan.model';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';

@Component({
	selector: 'device-component',
	templateUrl: 'device.component.html',
	styleUrls: ['./device.component.scss'],
})
export class DeviceComponent implements OnInit {
	public deviceForm: IFormGroup<IDevice>;
	public device: IDevice;
	public action: Action;
	public deviceTypes: Array<IDeviceType>;
	public deviceStatus: Array<{ value: string; label: string }>;
	public readonly DeviceStatus: typeof DeviceStatus = DeviceStatus;
	public deviceHistory: Array<ILoan>;

	public readonly Action: typeof Action = Action;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly deviceTypeProvider: DeviceTypeProvider,
		private readonly deviceProvider: DeviceProvider,
		private readonly navigationService: NavigationService,
		private readonly validationService: ValidationService,
		private readonly navigationDataManager: NavigationDataManager
	) {}

	public ngOnInit(): void {
		this.initializeForm();
		this.getDeviceTypes();
		this.getDeviceStatus();
	}

	public async createOrUpdateDevice(): Promise<void> {
		if (formIsValid(this.deviceForm, DeviceComponent)) {
			const device = this.deviceForm.value;
			this.action === Action.CREATE
				? await this.deviceProvider.create(device as IDevice)
				: await this.deviceProvider.update(device.id, device as IDevice);

			this.navigationService.navigateBack({ action: this.action, device });
		}
	}

	public async deleteDevice(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('DEVICE')) {
			await this.deviceProvider.delete(this.device.id);
			this.navigationService.navigateBack({ action: this.action });
		}
	}

	public async endLoan(device?: IDevice): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
			device,
			loan: device?.Loans[0],
		});
	}

	public async refreshDeviceData(): Promise<void> {
		this.device = this.navigationService.getNavigationData(NavigationDataKey.device);
	}

	public async createDeviceType(): Promise<void> {
		void this.navigationService
			.navigateTo<{ deviceType: IDeviceType }>(NavigationType.modal, DeviceTypeComponent, {
				action: Action.CREATE,
			})
			.then((result) => {
				if (result?.deviceType) {
					this.deviceTypes.push(result.deviceType);
					this.deviceForm.get('DeviceTypeId').setValue(result.deviceType.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.deviceForm, this.deviceForm);
			})
			.catch();
	}

	public async openLoan(loan?: ILoan): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
			loan,
		});
	}

	private initializeForm(): void {
		this.action = this.navigationService.getNavigationData(NavigationDataKey.action);
		this.device = this.navigationService.getNavigationData(NavigationDataKey.device);
		if (this.device) {
			this.deviceHistory = this.device.History;
		}

		const state = this.navigationDataManager.getData<IFormGroup<IDevice>>(NavigationDataKey.deviceForm);

		if (state) {
			this.deviceForm = state;
		} else {
			this.deviceForm = DeviceFormFactory.create(this.device);
		}
	}

	/**
	 * Get the deviceTypes from the API
	 */
	private async getDeviceTypes(): Promise<void> {
		this.deviceTypes = await this.deviceTypeProvider.getAll();
	}

	/**
	 * Get the device status based on the {@link DeviceStatus} enum
	 * formatted for the form-select
	 */
	private getDeviceStatus(): void {
		this.deviceStatus = Object.keys(DeviceStatus).map((key) => ({
			value: DeviceStatus[key],
			label: translate(`DEVICE_STATUS.${key.toUpperCase()}`),
		}));
	}
}
